import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    Alert,
    Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getFirestore, collection, getDocs, query, orderBy, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const firestore = getFirestore();
const unidades = ['UN', 'MTS', 'PAR', 'KG', 'LT', 'Pcs'];

function ListMaterialesHuawei() {
    const [materiales, setMateriales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [formData, setFormData] = useState({
        codigo: '',
        descripcion: '',
        unidad: 'UN'
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, material: null });

    useEffect(() => {
        fetchMateriales();
    }, []);

    const fetchMateriales = async () => {
        try {
            setLoading(true);
            const q = query(
                collection(firestore, 'materiales_huawei'),
                orderBy('id', 'asc')
            );
            const querySnapshot = await getDocs(q);
            const materialesData = [];
            
            querySnapshot.forEach((docSnap) => {
                materialesData.push({ ...docSnap.data(), docId: docSnap.id });
            });
            
            setMateriales(materialesData);
        } catch (error) {
            console.error('Error al cargar materiales:', error);
            showSnackbar('Error al cargar los materiales', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (material = null) => {
        if (material) {
            setEditingMaterial(material);
            setFormData({
                codigo: material.codigo || '',
                descripcion: material.descripcion || '',
                unidad: material.unidad || 'UN'
            });
        } else {
            setEditingMaterial(null);
            setFormData({
                codigo: '',
                descripcion: '',
                unidad: 'UN'
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingMaterial(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            // Validaciones
            if (!formData.codigo || !formData.descripcion) {
                showSnackbar('Código y descripción son obligatorios', 'warning');
                return;
            }

            const dataToSave = {
                codigo: formData.codigo,
                descripcion: formData.descripcion,
                unidad: formData.unidad
            };

            if (editingMaterial) {
                // Actualizar material existente
                const docRef = doc(firestore, 'materiales_huawei', editingMaterial.docId);
                await updateDoc(docRef, dataToSave);
                showSnackbar('Material actualizado exitosamente', 'success');
            } else {
                // Crear nuevo material
                // Obtener el ID más alto para asignar el siguiente
                const maxId = materiales.length > 0 
                    ? Math.max(...materiales.map(m => parseInt(m.id) || 0))
                    : 0;
                
                dataToSave.id = (maxId + 1).toString();
                await addDoc(collection(firestore, 'materiales_huawei'), dataToSave);
                showSnackbar('Material creado exitosamente', 'success');
            }

            handleCloseDialog();
            fetchMateriales();
        } catch (error) {
            console.error('Error al guardar material:', error);
            showSnackbar('Error al guardar el material', 'error');
        }
    };

    const handleDelete = async (material) => {
        setDeleteConfirm({ open: true, material });
    };

    const confirmDelete = async () => {
        try {
            const docRef = doc(firestore, 'materiales_huawei', deleteConfirm.material.docId);
            await deleteDoc(docRef);
            showSnackbar('Material eliminado exitosamente', 'success');
            setDeleteConfirm({ open: false, material: null });
            fetchMateriales();
        } catch (error) {
            console.error('Error al eliminar material:', error);
            showSnackbar('Error al eliminar el material', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const filteredMateriales = materiales.filter(material => {
        const searchLower = searchTerm.toLowerCase();
        return (
            material.codigo?.toLowerCase().includes(searchLower) ||
            material.descripcion?.toLowerCase().includes(searchLower) ||
            material.unidad?.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Materiales - Huawei
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Nuevo Material
                    </Button>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Buscar por código, descripción o unidad"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Ej: 20100161, FIBRA ÓPTICA, MTS..."
                    />
                </Box>

                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Chip label={`Total: ${filteredMateriales.length} materiales`} color="primary" />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredMateriales.map((material) => (
                                <TableRow 
                                    key={material.docId}
                                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                                >
                                    <TableCell>{material.id}</TableCell>
                                    <TableCell>{material.codigo}</TableCell>
                                    <TableCell sx={{ maxWidth: 500 }}>{material.descripcion}</TableCell>
                                    <TableCell>
                                        <Chip label={material.unidad} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => handleOpenDialog(material)}
                                            title="Editar"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDelete(material)}
                                            title="Eliminar"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredMateriales.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No se encontraron resultados{searchTerm && ` para "${searchTerm}"`}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Dialog para Crear/Editar */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingMaterial ? 'Editar Material' : 'Nuevo Material'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Código *"
                                    name="codigo"
                                    value={formData.codigo}
                                    onChange={handleInputChange}
                                    placeholder="Ej: 20100161"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Unidad *"
                                    name="unidad"
                                    value={formData.unidad}
                                    onChange={handleInputChange}
                                >
                                    {unidades.map((unidad) => (
                                        <MenuItem key={unidad} value={unidad}>
                                            {unidad}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descripción *"
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                    placeholder="Descripción del material"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        color="primary"
                    >
                        {editingMaterial ? 'Actualizar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmación de Eliminación */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, material: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar el material "{deleteConfirm.material?.descripcion}"?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta acción no se puede deshacer.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, material: null })}>
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListMaterialesHuawei;
